import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, Prompt, useHistory } from 'react-router-dom';

import {
  getBorrowerIdFromCreditAgreement,
  isCreditAgreementMutateAllowedAdapter,
  isWipModified,
} from '@finley-technologies/credit-agreement-utils';

import {
  OrganizationGQLType,
  TAccountGQLType,
  useGetUserQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { dashboardRoutePadding } from 'packages/common-frontend/constants';
import { useIsCreditAgreementWipUnlockedQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import {
  CaWipExitPromptMessage,
  creditAgreementFullArtifactPartyMap,
  creditAgreementFullArtifactPropertyMap,
  propertyTypeContainerToPropertyMap,
} from 'packages/creditAgreements/ui/consts';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementFullArtifactSection } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/CreditAgreementFullArtifactSection';
import { CreditAgreementContextProvider } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks';
import { useSaveLenderWipCreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks/useSaveLenderWipCreditAgreement';
import { CreditAgreementWipStickyHeader } from 'packages/creditAgreements/ui/creditAgreementWip/CreditAgreementWipStickyHeader';
import {
  createBankAccountsMap,
  createCapitalProvidersMap,
  createOrganizationMap,
} from 'packages/creditAgreements/ui/creditAgreementWip/utils/createMaps';
import { createCreditAgreementFullArtifactWipShell } from 'packages/creditAgreements/ui/creditAgreementWip/utils/mutateWipMetadata';
import { useGetCommonWipData } from 'packages/creditAgreements/ui/creditAgreementWip/utils/useGetCommonWipData';
import { useGetCurrentCaAndWip } from 'packages/creditAgreements/ui/creditAgreementWip/utils/useGetCurrentCaAndWip';
import { wipExitAlertCallback } from 'packages/creditAgreements/ui/creditAgreementWip/utils/wipAlertCallback';
import { createWipSchemaValidation } from 'packages/creditAgreements/ui/creditAgreementWip/utils/wipSchemaValidation';
import {
  LoanProductsMap,
  OrganizationIdToBankAccountsMap,
  OrganizationIdToUsersMap,
  OrganizationMap,
} from 'packages/creditAgreements/ui/types';
import {
  SettingsFacilityBorrowingBaseTabOptionKeys,
  SettingsFacilityBorrowingBaseTemplate,
} from 'packages/settings/ui/SettingsFacilityBorrowingBaseTemplate';
import { SettingsFacilityPortfolioCovenantsTemplate } from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsTemplate';
import { settingsPaths } from 'packages/sidebar/ui/sideBarUtils';
import { AdvanceRateTemplate } from 'valleyBank/domains/AdvanceRate/ui/details/AdvanceRateTemplate';

const CreditAgreementSectionTemplate: React.FC<{
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  creditFacilityId: string;
  isEditOnlyMode?: boolean;
}> = ({ section, creditFacilityId, children, isEditOnlyMode }) => {
  if (section === 'borrowingBaseMechanics') {
    return (
      <SettingsFacilityBorrowingBaseTemplate
        creditFacilityId={creditFacilityId}
        selectedTab={SettingsFacilityBorrowingBaseTabOptionKeys.configuration}
      >
        {children}
      </SettingsFacilityBorrowingBaseTemplate>
    );
  }

  if (section === 'portfolioCovenants' && !isEditOnlyMode) {
    return (
      <SettingsFacilityPortfolioCovenantsTemplate
        creditFacilityId={creditFacilityId}
      >
        {children}
      </SettingsFacilityPortfolioCovenantsTemplate>
    );
  }

  return <>{children}</>;
};

const getOrganizationsFromWip = (
  wip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): Pick<OrganizationGQLType, 'id' | 'name' | 'slug'>[] => {
  const orgs: Pick<OrganizationGQLType, 'id' | 'name' | 'slug'>[] = [];

  Object.values(wip.facilityMechanics.parties).forEach((parties) => {
    parties.forEach((party) => {
      orgs.push(party.entity);
    });
  });
  return orgs;
};

const getOrganizationIdToUsersMapFromWip = (
  wip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): OrganizationIdToUsersMap =>
  Object.values(wip.facilityMechanics.parties).reduce<OrganizationIdToUsersMap>(
    (acc, parties) => {
      parties.forEach((party) => {
        if (!acc[party.entity.id]) {
          // eslint-disable-next-line no-param-reassign
          acc[party.entity.id] = [];
        }
        party.users.forEach((user) => {
          acc[party.entity.id].push({
            id: user.entity.id,
            firstName: user.entity.firstName,
            lastName: user.entity.lastName,
            email: user.entity.email,
          });
        });
      });
      return acc;
    },
    {},
  );

const getOrganizationIdToBankAccountsMapFromWip = (
  wip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): OrganizationIdToBankAccountsMap =>
  Object.values(
    wip.facilityMechanics.parties,
  ).reduce<OrganizationIdToBankAccountsMap>((acc, parties) => {
    parties.forEach((party) => {
      if (!acc[party.entity.id]) {
        // eslint-disable-next-line no-param-reassign
        acc[party.entity.id] = [];
      }
      if (
        party.type === CreditAgreement.DomainEntity.Items.PartyType.Borrower ||
        party.type ===
          CreditAgreement.DomainEntity.Items.PartyType.CapitalProvider
      ) {
        party.bankAccounts.forEach((ba) => {
          acc[party.entity.id].push({
            id: ba.entity.id,
            accountNumber: ba.entity.accountNumber,
            routingNumber: ba.entity.routingNumber,
            name: ba.entity.name,
            institutionName: ba.entity.institutionName,
          });
        });
      }
    });
    return acc;
  }, {});

const getArtifactSlugsFromWip = (
  wip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): string[] => {
  const artifactSlugs = new Set<string>();

  const allDeliverableArtifactConfigs = _.flattenDeep(
    wip.deliverables.map((d) => _.concat(d.finalArtifacts, d.dependencies)),
  ).map((a) => a._config_);

  for (const config of allDeliverableArtifactConfigs) {
    switch (config.type) {
      case 'supplementalTape':
        artifactSlugs.add(config.artifactSlug);
        break;
      default:
        break;
    }
  }
  return Array.from(artifactSlugs);
};

const orgProvidedKeysFromWip = (
  wip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): string[] => {
  const { portfolioCovenants, deliverables } = wip;

  const orgProvidedKeys = new Set<string>();

  portfolioCovenants.forEach((cov) => {
    if (cov._config_) {
      cov._config_.postgresQueries.forEach((query) => {
        if (query.type === 'orgProvidedInput') {
          orgProvidedKeys.add(query.parameterConfig.key.key);
        }
      });
    }
  });

  const allDeliverableArtifactConfigs = _.flattenDeep(
    deliverables.map((d) => _.concat(d.finalArtifacts, d.dependencies)),
  ).map((a) => a._config_);

  allDeliverableArtifactConfigs.forEach((config) => {
    if (config.type === 'orgProvidedInput') {
      orgProvidedKeys.add(config.key);
    }
  });

  return Array.from(orgProvidedKeys);
};

// introspection entity fetch parallels "CreditAgreementFullArtifactWithDynamicEntityFetch"
// on admin dashboard, but instantiates wip config interface with information
// already existing within the CA wip, as lender cannot have full access to all
// organizations, users, and bank accounts
const CreditAgreementFullArtifactSectionWithIntrospectionEntityFetch: React.FC<{
  creditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  organizationMap: OrganizationMap;
  tAccounts: TAccountGQLType[];
  formProps: FormikProps<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>;
  creditFacilityId: string;
  arePartiesLocked: boolean;
  setArePartiesLocked: React.Dispatch<React.SetStateAction<boolean>>;
  loanProductsMap: LoanProductsMap;
  operationMode: CreditAgreement.OperationMode;
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  isEditOnlyMode?: boolean;
}> = ({
  creditAgreementFullArtifact,
  organizationMap,
  tAccounts,
  formProps,
  creditFacilityId,
  arePartiesLocked,
  setArePartiesLocked,
  loanProductsMap,
  operationMode,
  section,
  isEditOnlyMode = false,
}) => {
  const [capitalProvidersMap, setCapitalProvidersMap] = useState(
    createCapitalProvidersMap(
      creditAgreementFullArtifact.facilityMechanics.parties.capitalProviders,
    ),
  );

  const bankAccountsMap = createBankAccountsMap(
    creditAgreementFullArtifact.facilityMechanics.parties,
  );

  const [isFormDirty, setIsFormDirty] = useState(formProps.dirty);

  useEffect(() => {
    if (isFormDirty !== formProps.dirty) {
      setIsFormDirty(formProps.dirty);
    }
  }, [formProps, isFormDirty]);

  // Display browser warning popup when user attempts to manually refresh or close page
  useEffect(() => {
    if (isFormDirty) {
      window.addEventListener('beforeunload', wipExitAlertCallback);
    }
    // remove exit prompt event listener when unmounting
    return () => {
      window.removeEventListener('beforeunload', wipExitAlertCallback);
    };
  }, [isFormDirty]);

  const organizationIdToUsersMap = getOrganizationIdToUsersMapFromWip(
    formProps.values,
  );

  const organizationIdToBankAccountsMap = getOrganizationIdToBankAccountsMapFromWip(
    formProps.values,
  );

  const orgProvidedInputKeys = orgProvidedKeysFromWip(formProps.values);

  const wipShell = createCreditAgreementFullArtifactWipShell(creditFacilityId);

  const portfolioCovenantKeys = creditAgreementFullArtifact.portfolioCovenants.map(
    (cov) => cov.covenantKey,
  );

  const reportNamesMap: { [K: string]: string[] } = {};

  const allReportConfigTypes =
    CreditAgreement.DomainEntity.ReportConfigTypeIterable;
  for (const reportConfigType of allReportConfigTypes) {
    const reportNames = creditAgreementFullArtifact.reportingMechanics
      .filter((report) => report.reportConfigTypes.includes(reportConfigType))
      .map((report) => report.name);

    reportNamesMap[reportConfigType] = reportNames;
  }

  const {
    saveAndUpdateConfigCreditAgreementWip,
    saveCreditAgreementFullArtifactWip,
    isSaveCreditAgreementFullArtifactWipLoading,
  } = useSaveLenderWipCreditAgreement({ creditFacilityId });

  return (
    <>
      <Prompt when={isFormDirty} message={CaWipExitPromptMessage} />
      <CreditAgreementFullArtifactSection
        creditAgreementFullArtifact={creditAgreementFullArtifact}
        section={section}
        wipConfig={{
          formProps,
          wipShell,
          capitalProvidersMap,
          creditAgreementFullArtifactPropertyMap,
          creditAgreementFullArtifactPartyMap,
          loanProductsMap,
          organizationMap,
          tAccounts,
          organizationIdToUsersMap,
          organizationIdToBankAccountsMap,
          propertyTypeContainerToPropertyMap,
          arePartiesLocked,
          orgProvidedInputKeys,
          setArePartiesLocked,
          setCapitalProvidersMap,
          isMutateAllowed: isCreditAgreementMutateAllowedAdapter(operationMode),
          artifactSlugs: getArtifactSlugsFromWip(formProps.values),
          portfolioCovenantKeys,
          reportNamesMap,
          saveAndUpdateConfigCreditAgreementWip,
          saveCreditAgreementFullArtifactWip,
          isSaveCreditAgreementFullArtifactWipLoading,
          operationMode,
          bankAccountsMap,
        }}
        isEditOnlyMode={isEditOnlyMode}
      />
    </>
  );
};

const CreditAgreementDocumentSectionWip: React.FC<{
  creditAgreementFullArtifactWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  currentCreditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  isEditOnlyMode?: boolean;
}> = withLDConsumer()(
  ({
    creditAgreementFullArtifactWip,
    currentCreditAgreementFullArtifact,
    section,
    isEditOnlyMode,
    flags,
  }) => {
    const history = useHistory();

    const [isDiffOpen, setIsDiffOpen] = useState<boolean>(false);
    const [isJsonUploadOpen, setIsJsonUploadOpen] = useState(false);
    const [arePartiesLocked, setArePartiesLocked] = useState(true);

    const { creditFacilityId } = creditAgreementFullArtifactWip;

    const borrowerId = getBorrowerIdFromCreditAgreement(
      creditAgreementFullArtifactWip,
    );

    // wip has already been modified by BE to contain latest artifactDocumentId
    const artifactDocumentId =
      creditAgreementFullArtifactWip.borrowingBaseMechanics.calcAgentMechanics
        ._artifactDocumentId_ || null;

    const {
      creditFacilitySlug,
      tAccounts,
      loanProductsMap,
    } = useGetCommonWipData({
      creditFacilityId,
    });

    const {
      saveCreditAgreementFullArtifactWip,
      isSaveCreditAgreementFullArtifactWipLoading,
    } = useSaveLenderWipCreditAgreement({ creditFacilityId });

    const isWipDirtyCallback = useCallback(
      (newWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact) => {
        return isWipModified(creditAgreementFullArtifactWip, newWip);
      },
      [creditAgreementFullArtifactWip],
    );

    const { data: userData } = useGetUserQuery();

    if (
      !creditFacilitySlug ||
      !tAccounts ||
      !loanProductsMap ||
      !userData ||
      !userData.user
    ) {
      return <Skeleton />;
    }

    const organizations = getOrganizationsFromWip(
      creditAgreementFullArtifactWip,
    );
    const organizationMap = createOrganizationMap(organizations);

    const operationMode: CreditAgreement.OperationMode = 'lender';

    const onCommitDone = () => {
      if (!flags?.isValleyBankLenderView) {
        history.push(settingsPaths.facilities);
      } else {
        history.push('/');
      }
    };

    // @vb save wip permission not explicitly used because there is no read only role type

    // @vb this is the cleanest way to determine if the user can commit the credit agreement without having to prop drill permissions down
    // need to rethink the best way to handle this
    const isCreditAgreementCommitAllowed = flags?.isValleyBankLenderView
      ? userData.user.roleType === 'ADMIN' // only finley & stacey can commit for valley bank lender view
      : true;

    return (
      <Box>
        <Formik
          initialValues={creditAgreementFullArtifactWip}
          onSubmit={() => {}}
          validationSchema={createWipSchemaValidation()}
          validateOnChange
          validateOnMount
          enableReinitialize // allows the entire credit agreement formik initialValues to be reset to current values upon rerender (eg. on saves)
        >
          {(formProps) => {
            const isWipDirty = isWipDirtyCallback(formProps.values);

            return (
              <Form>
                <CreditAgreementContextProvider
                  initOrgSlugs={organizations.map(({ slug }) => slug)}
                  parties={
                    creditAgreementFullArtifactWip.facilityMechanics.parties
                  }
                >
                  {/* maxH required for second level sticky header to work
                since position:sticky attaches to the nearest ancestor with a scrolling mechanism
                https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll */}
                  <Box overflowY="auto" maxH="100vh">
                    <CreditAgreementWipStickyHeader
                      isWipDirty={isWipDirty}
                      isDiffOpen={isDiffOpen}
                      setIsDiffOpen={setIsDiffOpen}
                      creditFacilityId={creditFacilityId}
                      borrowerId={borrowerId}
                      formProps={formProps}
                      artifactDocumentId={artifactDocumentId}
                      creditFacilitySlug={creditFacilitySlug}
                      onCommitDone={onCommitDone}
                      currentCreditAgreementFullArtifact={
                        currentCreditAgreementFullArtifact
                      }
                      isJsonUploadOpen={isJsonUploadOpen}
                      setIsJsonUploadOpen={setIsJsonUploadOpen}
                      operationMode={operationMode}
                      saveCreditAgreementFullArtifactWip={
                        saveCreditAgreementFullArtifactWip
                      }
                      isSaveCreditAgreementFullArtifactWipLoading={
                        isSaveCreditAgreementFullArtifactWipLoading
                      }
                      isCommitAllowed={isCreditAgreementCommitAllowed}
                    />

                    {flags?.isValleyBankLenderView ? (
                      <AdvanceRateTemplate>
                        <CreditAgreementFullArtifactSectionWithIntrospectionEntityFetch
                          creditAgreementFullArtifact={formProps.values}
                          organizationMap={organizationMap}
                          creditFacilityId={creditFacilityId}
                          formProps={formProps}
                          arePartiesLocked={arePartiesLocked}
                          setArePartiesLocked={setArePartiesLocked}
                          tAccounts={tAccounts}
                          loanProductsMap={loanProductsMap}
                          operationMode={operationMode}
                          section={section}
                        />
                      </AdvanceRateTemplate>
                    ) : (
                      <CreditAgreementSectionTemplate
                        creditFacilityId={creditFacilityId}
                        section={section}
                        isEditOnlyMode={isEditOnlyMode}
                      >
                        <CreditAgreementFullArtifactSectionWithIntrospectionEntityFetch
                          creditAgreementFullArtifact={formProps.values}
                          organizationMap={organizationMap}
                          creditFacilityId={creditFacilityId}
                          formProps={formProps}
                          arePartiesLocked={arePartiesLocked}
                          setArePartiesLocked={setArePartiesLocked}
                          tAccounts={tAccounts}
                          loanProductsMap={loanProductsMap}
                          operationMode={operationMode}
                          section={section}
                          isEditOnlyMode={isEditOnlyMode}
                        />
                      </CreditAgreementSectionTemplate>
                    )}
                  </Box>
                </CreditAgreementContextProvider>
              </Form>
            );
          }}
        </Formik>
      </Box>
    );
  },
);

export const CreditAgreementSectionWip: React.FC<{
  creditFacilityId: string;
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  isEditOnlyMode?: boolean;
}> = ({ section, creditFacilityId, isEditOnlyMode }) => {
  const toast = useToast();

  const {
    creditAgreementFullArtifactWip,
    currentCreditAgreementFullArtifact,
  } = useGetCurrentCaAndWip({
    creditFacilityId,
    creditAgreementsPath: '/credit-agreements',
  });

  const {
    data: creditAgreementWipUnlockedData,
  } = useIsCreditAgreementWipUnlockedQuery({
    variables: {
      creditFacilityId,
    },
    onError: () => {
      toast({
        title: `Error loading is credit agreement WIP unlocked.`,
        status: 'error',
        position: 'top',
        isClosable: true,
      });
    },
    fetchPolicy: 'network-only',
  });

  if (
    !creditAgreementWipUnlockedData ||
    !creditAgreementFullArtifactWip ||
    !currentCreditAgreementFullArtifact
  ) {
    return <Skeleton />;
  }

  const isCreditAgreementWipUnlocked =
    creditAgreementWipUnlockedData.creditFacility.isCreditAgreementWipUnlocked;

  return (
    <>
      {isCreditAgreementWipUnlocked ? (
        <CreditAgreementDocumentSectionWip
          creditAgreementFullArtifactWip={creditAgreementFullArtifactWip}
          currentCreditAgreementFullArtifact={
            currentCreditAgreementFullArtifact
          }
          section={section}
          isEditOnlyMode={isEditOnlyMode}
        />
      ) : (
        <Box padding={dashboardRoutePadding}>
          <Alert status="warning" d="flex" w="fit-content">
            <AlertIcon />
            <AlertDescription>
              <Flex>
                <Text>Remove the&nbsp;</Text>
                <Link
                  to="/credit-agreements"
                  style={{ textDecoration: 'underline' }}
                >
                  existing future-dated credit agreement
                </Link>
                <Text>&nbsp;to enable configuration.</Text>
              </Flex>
            </AlertDescription>
          </Alert>
        </Box>
      )}
    </>
  );
};

export const CreditAgreementSectionWipContainer: React.FC<{
  creditFacilityId: string;
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  isEditOnlyMode?: boolean;
}> = ({ section, creditFacilityId, isEditOnlyMode }) => {
  const toast = useToast();

  const {
    data: creditAgreementWipUnlockedData,
  } = useIsCreditAgreementWipUnlockedQuery({
    variables: {
      creditFacilityId,
    },
    onError: () => {
      toast({
        title: `Error loading is credit agreement WIP unlocked.`,
        status: 'error',
        position: 'top',
        isClosable: true,
      });
    },
    fetchPolicy: 'network-only',
  });

  if (!creditAgreementWipUnlockedData) {
    return <Skeleton />;
  }

  const isCreditAgreementWipUnlocked =
    creditAgreementWipUnlockedData.creditFacility.isCreditAgreementWipUnlocked;

  return (
    <>
      {isCreditAgreementWipUnlocked ? (
        <CreditAgreementSectionWip
          creditFacilityId={creditFacilityId}
          section={section}
          isEditOnlyMode={isEditOnlyMode}
        />
      ) : (
        <Box padding={dashboardRoutePadding}>
          <Alert status="warning" d="flex" w="fit-content">
            <AlertIcon />
            <AlertDescription>
              <Flex>
                <Text>Remove the&nbsp;</Text>
                <Link
                  to="/credit-agreements"
                  style={{ textDecoration: 'underline' }}
                >
                  existing future-dated credit agreement
                </Link>
                <Text>&nbsp;to enable configuration.</Text>
              </Flex>
            </AlertDescription>
          </Alert>
        </Box>
      )}
    </>
  );
};
